import React, { useState } from 'react';
import classNames from 'classnames';
import { FormField } from '@workday/canvas-kit-react/form-field';
import { TertiaryButton } from '@workday/canvas-kit-react/button';
import { TextInput } from '@workday/canvas-kit-react/text-input';
import { visibleIcon, invisibleIcon } from '@workday/canvas-system-icons-web';
import { autoId } from '@workday/upc-common';
import styles from './PasswordTextField.module.scss';
import { ID_SHOW_PASSWORD_ICON } from '../../autoIds';

interface PasswordTextFieldProps {
    autoFocus?: boolean; // True if the autofocus attribute should be set
    autoComplete?: string; // autocomplete property of the password input
    className?: string | any; // CSS class to add for the component
    errorMessage?: string; // Hint for fixing the validation error
    handlers: object; // Event handlers
    id: string; // The HTML `id` and `name` of the input field. Also used to generate test id's.
    isDisabled?: boolean; // True if the field should be disabled
    isInvalid?: boolean; // True if the value is not in a valid format
    label?: string; // Text of the form field
    value?: string; // Value of the password
    errorLabel?: string; // Text for error prefix
    placeholder?: string; // Placeholder for the input
    passwordInputRef?: any; // Ref for password input
    passwordInputProps?: object; // Input props for password input
}

// canvas injects an id into this container when it is used inside a FormField with the inputId prop
const Container = ({
    autoComplete,
    autoFocus,
    inputRef,
    id, // this id is injected by canvas
    isDisabled,
    handlers,
    value,
    inputProps,
    placeholder,
    ...rest
}) => {
    const [showPassword, setShowPassword] = useState(false);

    return (
        <div className={styles.container}>
            <TextInput
                placeholder={placeholder}
                className={styles.textInput}
                autoComplete={autoComplete}
                autoFocus={autoFocus}
                {...autoId(id)}
                disabled={isDisabled}
                grow={true}
                id={id}
                name={id}
                {...handlers}
                ref={inputRef}
                {...inputProps}
                type={showPassword ? 'text' : 'password'}
                value={value}
                {...rest}
            />
            <TertiaryButton
                aria-label={'Show Password'}
                icon={showPassword ? invisibleIcon : visibleIcon}
                size='small'
                className={styles.iconButton}
                data-testid={ID_SHOW_PASSWORD_ICON}
                role='switch'
                type='button'
                disabled={isDisabled}
                aria-checked={showPassword}
                onClick={() => setShowPassword(!showPassword)}
            />
        </div>
    );
};

export const PasswordTextField = ({
    placeholder = 'Enter your password',
    autoFocus,
    autoComplete = 'off',
    className,
    errorMessage,
    handlers = {},
    id,
    isDisabled,
    isInvalid,
    errorLabel,
    label = 'Create a Password',
    value,
    passwordInputRef,
    passwordInputProps = {}
}: PasswordTextFieldProps) => {
    const classes = classNames(styles.formField, {
        [className]: !!className
    });

    return (
        <FormField
            className={classes}
            error={isInvalid ? FormField.ErrorType.Error : undefined}
            grow={true}
            hintId={isInvalid ? 'error-' + id : undefined}
            hintText={isInvalid && errorMessage}
            inputId={id}
            errorLabel={errorLabel}
            label={label}>
            {/* @ts-ignore */}
            <Container
                placeholder={placeholder}
                autoFocus={autoFocus}
                autoComplete={autoComplete}
                isDisabled={isDisabled}
                value={value}
                inputRef={passwordInputRef}
                inputProps={passwordInputProps}
                handlers={handlers}
            />
        </FormField>
    );
};
